import type { MeResource } from "@sarva/api-client";
import { PermissionProvider, Spinner, TimeZoneProvider } from "@sarva/ui";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  setActivePracticeId,
  useSessionState,
  type KeyScope,
} from "@/shared/api";
import { Navigate, useLocation } from "react-router-dom";
import { ProblemNotice } from "@/shared/ProblemNotice";
import { Brand } from "@/shared/layout/Brand";
import { useMe } from "../hooks/useMe";
import { sessionTimeZone } from "../hooks/useSessionTimeZone";

/**
 * Real identity drives the UI.
 *
 * (H) doc 27 §7: the API is the authority and the UI merely avoids offering
 * impossible actions. That only holds if the UI's idea of "possible" is the
 * API's — so `<PermissionProvider>` is fed the **resolved** permission set from
 * `GET /me` and from nothing else. There is no mock permission source in this
 * application any more; the one that existed is deleted rather than left beside
 * this as a fallback, because a fallback permission list is how a gate ends up
 * passing for a user the API will refuse.
 */
export interface AppSession {
  me: MeResource;
  /** (H) doc 27 §5: every query key carries these two. */
  scope: KeyScope;
  timeZone: string;
}

const SessionContext = createContext<AppSession | null>(null);

export function useSession(): AppSession {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error("useSession must be used inside <SessionProvider>.");
  }
  return session;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { practiceId } = useSessionState();
  const me = useMe();
  // Straight off `/me`, which carries the zone on both the active practice and
  // the tenant. This used to be a second request for the full practice resource
  // with a browser-zone fallback, and that fallback was reached by every
  // tenant-wide session — see the hook.
  //
  // The practice whose timezone applies is the one the API says is active, not
  // the one this client asked for: they agree when a selection was accepted, and
  // when the identity has a server-side default with no selection made, only the
  // API knows it.
  const timeZone = sessionTimeZone(me.data);

  /**
   * (H) GAP-080 — a person belonging to exactly one practice is never asked which one.
   *
   * Half the product needs a practice before it can answer anything: the scoreboard, the
   * schedule, the import mappings, the settings screen. With none chosen those render
   * "choose a practice", which for a single-practice identity is a question with one possible
   * answer, asked on every first load and after every sign-in.
   *
   * **Only when there is exactly one.** A tenant-wide session across several practices is not
   * an unanswered question — "all practices" is a real and useful scope, and picking one of
   * them on the person's behalf would silently narrow figures they asked to see whole. The
   * group rollup those sessions want is a separate build, not a default.
   *
   * It is written to the store rather than derived, because the store is what the practice
   * switcher, the query keys and the `X-Sarva-Practice-Id` header all read: deriving it here
   * would leave the switcher showing nothing selected while every request carried a practice.
   */
  // Backend D206: an organisation with one practice (or a person who can work in only one) is
  // put in it — the same as a single practice-scoped grant always was.
  const workable = me.data?.practices ?? [];
  const onlyPracticeId =
    workable.length === 1
      ? workable[0]?.id
      : me.data?.practice_ids.length === 1
        ? me.data.practice_ids[0]
        : undefined;
  useEffect(() => {
    if (practiceId === null && onlyPracticeId !== undefined) {
      setActivePracticeId(onlyPracticeId);
    }
  }, [practiceId, onlyPracticeId]);

  /**
   * D185 — a person's home practice opens a session that has no choice of its own.
   *
   * (H) **Once**, at the first `/me` this tab sees, and only when nothing was chosen yet. Choosing
   * "All practices" in the switcher also leaves the selection `null`; re-applying the home
   * practice on every `null` would snap the switcher back the moment somebody picked the whole
   * tenant, and make that option unselectable. The API returns only a home practice the caller
   * still covers, so this can never open a session the next request would refuse.
   */
  const homeChecked = useRef(false);
  const homePracticeId = me.data?.default_practice_id ?? null;
  useEffect(() => {
    if (homeChecked.current || me.data === undefined) return;
    homeChecked.current = true;
    if (practiceId === null && homePracticeId !== null) {
      setActivePracticeId(homePracticeId);
    }
  }, [me.data, practiceId, homePracticeId]);

  const session = useMemo<AppSession | null>(() => {
    if (me.data === undefined) return null;
    return {
      me: me.data,
      scope: { tenantId: me.data.tenant.id, practiceId },
      timeZone,
    };
  }, [me.data, practiceId, timeZone]);

  if (me.isError) {
    return (
      <div className="mx-auto max-w-prose py-16">
        <ProblemNotice error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }

  if (session === null) {
    return (
      // (H2) The first screen after signing in: the product's own mark on the canvas with a
      // quiet progress line, not a bare spinner in an empty page.
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-6 bg-canvas px-4"
        role="status"
        aria-live="polite"
      >
        <Brand className="h-9" decorative />
        <div className="flex flex-col items-center gap-3">
          <div className="h-1 w-48 overflow-hidden rounded-full bg-subtle" aria-hidden>
            <div className="h-full w-full animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
          </div>
          <span className="flex items-center gap-2 text-sm text-secondary">
            <Spinner label="Loading your account" size={4} />
            Opening your practice…
          </span>
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider value={session}>
      <PermissionProvider permissions={session.me.permissions}>
        <TimeZoneProvider timeZone={session.timeZone}>
          <MfaEnrolmentGate required={session.me.mfa_enrolment_required ?? false}>
            {children}
          </MfaEnrolmentGate>
        </TimeZoneProvider>
      </PermissionProvider>
    </SessionContext.Provider>
  );
}

/** Where a person sets up their second factor. */
export const MFA_ENROLMENT_ROUTE = "/security";

/**
 * Backend D199 — where a second factor is required and this person has none, the API has
 * reduced their session to enrolling one, so every other screen would be a wall of refusals.
 * Send them to the one screen that works. The API is the control; this is the signpost.
 */
function MfaEnrolmentGate({ required, children }: { required: boolean; children: ReactNode }) {
  const location = useLocation();
  if (required && location.pathname !== MFA_ENROLMENT_ROUTE) {
    return <Navigate to={MFA_ENROLMENT_ROUTE} replace />;
  }
  return <>{children}</>;
}
